
import logger from "@/utils/logger";
import { JSONValue } from '@/types/common';
/**
 * Error sources to categorize where errors originate from
 */
export enum ErrorSource {
  General = 'general',
  Authentication = 'authentication',
  Database = 'database',
  API = 'api',
  EdgeFunction = 'edge_function',
  UI = 'ui',
  Validation = 'validation',
  ErrorBoundary = 'error_boundary', // Add missing error source
  Auth = 'auth' // Add missing error source
}

/**
 * Options for error handling
 */
export interface ErrorHandlingOptions {
  /** Source of the error */
  source?: ErrorSource;
  /** Additional context about where the error occurred */
  context?: string;
  /** Whether this is a high-priority error that should be logged/reported */
  important?: boolean;
  /** URL where the error occurred */
  url?: string;
  /** Show toast notification */
  showToast?: boolean;
  /** Toast notification title */
  toastTitle?: string;
  /** Toast notification description */
  toastDescription?: string;
  /** Additional data to include with the error */
  data?: Record<string, JSONValue>;
  /** Related email address */
  email?: string;
  /** Related crawl ID */
  crawlId?: string;
}

/**
 * Centralized error handling function
 * @param error The error object
 * @param options Options for error handling
 */
export function handleError(error: Error | unknown, options: ErrorHandlingOptions = {}): void {
  const { 
    source = ErrorSource.General, 
    context = '', 
    important = false, 
    url = '',
    showToast = false,
    toastTitle,
    toastDescription,
    data,
    email,
    crawlId
  } = options;
  
  // Convert error to a proper Error object if it's not already
  const errorObj = error instanceof Error ? error : new Error(String(error));
  
  // Log the error with context information
  logger.error(
    `[ERROR][${source}]${context ? `[${context}]` : ''}${important ? '[IMPORTANT]' : ''}:`,
    errorObj,
    { 
      url, 
      timestamp: new Date().toISOString(),
      ...(data && { data }),
      ...(email && { email }),
      ...(crawlId && { crawlId })
    }
  );
  
  // Add toast notification if requested
  if (showToast && typeof window !== 'undefined') {
    // Toast notifications are handled elsewhere, just log for now
    logger.info('Toast notification would show:', {
      title: toastTitle || 'Error',
      description: toastDescription || errorObj.message
    });
  }
}
